import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  FuzzyLogoNewOrange,
  WarningIcon,
} from '../../../assets/svg/SVGComponents';

import { Container, Content, ErrorMessage } from '../styles';

interface IGenericErrorProps {
  /**
   * Renderiza fora do BrowserRouter (fallback do ErrorBoundary raiz).
   * Nesse modo é proibido usar qualquer hook de rota.
   */
  standalone?: boolean;
  onRetry?: () => void;
}

/**
 * Tela mostrada no lugar da página em branco quando algo quebra.
 *
 * REGRA: este componente precisa ser à prova de crash — nada de useAuth, de
 * chamadas ao `api` ou de acesso a `user.*`. Se ele quebrar, não sobra nada
 * para renderizar. `useTranslation` é seguro porque o i18n roda com
 * `useSuspense: false`.
 */
const GenericError: React.FC<IGenericErrorProps> = ({
  standalone = false,
  onRetry,
}) => {
  const { t } = useTranslation();

  return (
    <Container>
      <Content>
        <FuzzyLogoNewOrange />
        <ErrorMessage>
          <span className="error-icon">
            <WarningIcon width="5rem" strokeWidth="10" stroke="#ff9800" />
          </span>
          <h2>
            {t('errors.generic_title', {
              defaultValue: 'Ops, algo deu errado.',
            })}
          </h2>
          <p>
            {t('errors.generic_description', {
              defaultValue:
                'Não conseguimos carregar esta página. Tente novamente e, se o problema continuar, entre em contato com o suporte.',
            })}
          </p>

          <div className="actions">
            {onRetry && (
              <button type="button" className="primary" onClick={onRetry}>
                {t('errors.retry', { defaultValue: 'Tentar novamente' })}
              </button>
            )}

            <button type="button" onClick={() => window.location.reload()}>
              {t('errors.reload', { defaultValue: 'Recarregar a página' })}
            </button>

            {/*
              Navegação dura de propósito: em `standalone` a árvore do router
              pode ser justamente a que quebrou, então nada de <Link>.
            */}
            <button
              type="button"
              onClick={() => window.location.assign(standalone ? '/' : '/home')}
            >
              {t('errors.back_home', { defaultValue: 'Voltar ao início' })}
            </button>
          </div>
        </ErrorMessage>
      </Content>
    </Container>
  );
};

export default GenericError;
